import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { Text } from '@components/Text/Text';
import { Icon } from '@components/Icon/Icon';
import { css } from '@emotion/react';

const FIELD_WIDTH = {
  s: '200px',
  m: '280px',
  l: '344px',
  full: '100%',
};

export interface FieldBaseProps {
  /**
   * Title of the field.
   */
  title: string;
  /**
   * DOM element type for the field label.
   */
  titleAs?: 'label' | 'legend';
  /**
   * Description under the label, can be anything but should stay mainly text.
   */
  helperText?: ReactNode;
  /**
   * Error message, shown only if `isInvalid` is true.
   */
  invalidText?: string;
  /**
   * Define if the child input is valid or not.
   */
  isInvalid?: boolean;
  /**
   * Adds a required indicator next to the label.
   */
  isRequired?: boolean;
  /**
   * `id` of the child input.
   * Will be added as the `for` attribute of the label.
   */
  inputId?: string;
  /**
   * `id` to set on the helper text element.
   * Can be used to set `aria-describedby` on the contained input element.
   * Defaults to `inputId + '-helper'`
   */
  helperTextId?: string;
  /**
   * `id` to set on the invalid text element.
   * Can be used to set `aria-describedby` on the contained input element
   * Defaults to `inputId + '-invalid'`
   */
  invalidTextId?: string;
  /**
   * Specifies if the error icon should be rendered when the field is invalid.
   */
  showInvalidIcon?: boolean;
  /**
   * Size of the field. 200px/280px/344px/100%.
   */
  size?: keyof typeof FIELD_WIDTH;
  /**
   * Can be anything, should include an input.
   */
  children: ReactNode;
  /**
   * Styles override.
   */
  className?: string;
}

const BlockText = styled(Text)`
  display: block;
`;

interface ContainerProps {
  size: FieldBaseProps['size'];
  as: 'div' | 'fieldset';
  className: string;
}

const Container = styled.div<ContainerProps>`
  width: ${({ size }) => FIELD_WIDTH[size]};

  /* TODO remove once global styles from insights.scss are gone */
  border: none;
  margin: initial;
  padding: initial;
  border-radius: initial;
  min-width: initial;

  & > legend {
    border: none;
    padding: initial;
    margin-right: initial;
    margin-left: initial;
  }
`;

export const FieldBase = forwardRef<
  HTMLDivElement | HTMLFieldSetElement,
  FieldBaseProps
>(
  (
    {
      title,
      titleAs = 'label',
      helperText,
      invalidText,
      isInvalid = false,
      isRequired = false,
      inputId,
      helperTextId,
      invalidTextId,
      showInvalidIcon = false,
      size = 'm',
      className,
      children,
    }: FieldBaseProps,
    ref,
  ) => {
    return (
      <Container
        className={className}
        ref={
          ref as typeof titleAs extends 'legend'
            ? ForwardedRef<HTMLFieldSetElement>
            : ForwardedRef<HTMLDivElement>
        }
        size={size}
        as={titleAs === 'legend' ? 'fieldset' : 'div'}
      >
        <BlockText
          as={titleAs}
          textType="subtitle2"
          textColor="text_default"
          css={css`
            margin-bottom: ${helperText ? 0 : tokens.spacer.x1};
            text-transform: none; // temp solution until the old styles from IWA are removed
          `}
          {...(titleAs === 'label' ? { htmlFor: inputId } : {})}
        >
          {title}{' '}
          {isRequired && (
            <span
              css={css`
                color: ${tokens.color.text_danger};
              `}
            >
              *
            </span>
          )}
        </BlockText>
        {helperText && (
          <BlockText
            as="span"
            textType="caption"
            textColor="text_light"
            id={helperTextId}
            css={css`
              margin-bottom: ${tokens.spacer.x1};
            `}
          >
            {helperText}
          </BlockText>
        )}
        {children}
        {isInvalid && (
          <BlockText
            as="span"
            textType="caption"
            textColor="text_danger"
            id={invalidTextId}
            css={css`
              margin-top: ${tokens.spacer.x1};
            `}
          >
            {showInvalidIcon && (
              <Icon
                icon="warning_alt"
                inline
                size="m"
                color="text_danger"
                css={css`
                  margin-right: ${tokens.spacer.x0};
                `}
              />
            )}
            {invalidText}
          </BlockText>
        )}
      </Container>
    );
  },
);

FieldBase.displayName = 'FieldBase';
